import dayjs from 'dayjs'
import { orderController } from '~/apis/back/services'
import type { TripProductPackagePriceCalendarDTO, TripProductPriceCalendarDTO, TripProductPriceCalendarResponse } from '~/apis/back/types'

interface PriceCalendarState {
  loading: boolean
  isShow: boolean
  calendarData: TripProductPriceCalendarResponse
  params: {
    productId: string
    productSkuId: string
    supplierId: string
    supplierAttId: string
    skuName?: string
  }
  selected: {
    date: string
    packageId: string
  }
  currentPackages: TripProductPackagePriceCalendarDTO[]
  cashbackDesc: string
}

export function usePriceCalendarState() {
  const initState: PriceCalendarState = {
    loading: false,
    params: {
      productId: '',
      productSkuId: '',
      supplierId: '',
      supplierAttId: '',
    },
    isShow: false,
    calendarData: {},
    selected: {
      date: '',
      packageId: '',
    },
    currentPackages: [],
    cashbackDesc: '',
  }

  const state = useState('priceCalendarState', () => initState)
  const userStore = useUserStore()
  const filterStore = useFilterConditionsStore()
  const { locale } = useLocale()
  const isMobile = useMobileByScreen()

  const minDate = computed(() => dayjs(state.value.calendarData?.priceCalendars?.[0]?.date || '').toDate())
  const maxDate = computed(() => dayjs(minDate.value).add(3, 'month').toDate())

  const mappedCalendarData = computed(() => {
    return state.value.calendarData?.priceCalendars?.reduce((acc: Record<string, TripProductPriceCalendarDTO>, item) => {
      const { date = '' } = item
      acc[date] = { ...item }
      return acc
    }, {})
  })

  function isDisabledTicketOption(item: TripProductPackagePriceCalendarDTO) {
    return !state.value.currentPackages.some(i => i.packageId === item.packageId)
  }

  function isDisabledDate(item?: TripProductPriceCalendarDTO) {
    return (state.value.selected.packageId && !item?.packagePriceList?.some(i => i.packageId === state.value.selected.packageId)) || !item
  }

  async function fetchPriceCalendar() {
    state.value.loading = true
    const { data: result } = await orderController.orderGetPriceCalendarPost({
      locale: locale.value,
      currency: userStore.userInfo.currency,
      productId: state.value.params.productId,
      startDate: dayjs().format('YYYY-MM-DD'),
      endDate: dayjs().add(3, 'month').format('YYYY-MM-DD'),
      supplierId: Number(state.value.params.supplierId),
      productSkuId: state.value.params.productSkuId,
    })

    state.value.calendarData = result.value?.data as TripProductPriceCalendarResponse
    state.value.loading = false
  }

  watch(() => state.value.calendarData, (data: TripProductPriceCalendarResponse) => {
    const { detailDate } = filterStore.getRequestFilterConditions()
    const defaultDate = mappedCalendarData.value?.[detailDate] ? detailDate : data?.priceCalendars?.[0]?.date || ''
    state.value.selected.date = defaultDate
    state.value.currentPackages = mappedCalendarData.value?.[defaultDate]?.packagePriceList as TripProductPackagePriceCalendarDTO[]
    // 如果当前日期只有一个套餐，直接选中
    state.value.selected.packageId = state.value.currentPackages?.length === 1 ? state.value.currentPackages[0].packageId || '' : ''
  })

  watch(() => state.value.selected.date, (value) => {
    state.value.currentPackages = mappedCalendarData.value?.[value]?.packagePriceList as TripProductPackagePriceCalendarDTO[]
  })

  async function onConfirm(attrId?: number) {
    const { packageId, date } = state.value.selected
    await navigateTo({
      path: `/attractions/booking/${locale.value}.html`,
      query: {
        ...getCommonQuery(),
        packageId,
        attrId,
        supplierId: state.value.params.supplierId,
        productId: state.value.params.productId,
        supplierAttId: state.value.params.supplierAttId,
        travelDate: date,
        currency: userStore.userInfo.currency,
      },
    }, {
      open: isMobile.value
        ? undefined
        : {
            target: '_blank',
          },
    })
    state.value.isShow = false
  }

  function formatPrice(weekDate: any) {
    if (mappedCalendarData.value?.[weekDate]?.minPrice) {
      return toThousands(mappedCalendarData.value?.[weekDate]?.minPrice)
    }
    return '-'
  }

  return {
    state,
    getters: {
      minDate,
      maxDate,
      mappedCalendarData,
    },
    actions: {
      onConfirm,
      isDisabledTicketOption,
      isDisabledDate,
      fetchPriceCalendar,
      formatPrice,
    },
  }
}
